"use client";

import React, { useEffect, useRef, useTransition } from "react";
import { useQueryState } from "nuqs";
import { searchParams } from "@/app/types/search-params";
import { Button } from "@/app/components/ui/button";

export type PaginationControlsProps = {
  className?: string;
  /** Total pages, provided from server to clamp buttons */
  totalPages: number;
  /** Optional ID of element to scroll to after page change */
  scrollToId?: string;
};

export default function PaginationControls({
  className,
  totalPages,
  scrollToId,
}: PaginationControlsProps) {
  const [isPending, startTransition] = useTransition();
  const [page, setPage] = useQueryState(
    "seite",
    searchParams.seite.withOptions({ shallow: false, startTransition })
  );
  const shouldScrollRef = useRef(false);

  const currentPage = Number.isFinite(page) && page > 0 ? page : 1;

  const goTo = (next: number) => {
    const clamped = Math.min(Math.max(1, next), Math.max(1, totalPages));
    if (scrollToId) {
      shouldScrollRef.current = true;
    }
    setPage(clamped);
  };

  // Scroll nach Abschluss der Navigation
  useEffect(() => {
    if (!isPending && shouldScrollRef.current && scrollToId) {
      shouldScrollRef.current = false;
      const target = document.getElementById(scrollToId);
      if (target) {
        target.scrollIntoView({ behavior: "instant", block: "start" });
      }
    }
  }, [isPending, scrollToId]);

  return (
    <div
      className={`mt-6 flex items-center justify-center gap-2 ${className ?? ""}`}
      data-pending={isPending ? "" : undefined}
    >
      <Button
        type="button"
        variant="secondary"
        onClick={() => goTo(currentPage - 1)}
        disabled={currentPage <= 1 || isPending}
        aria-label="Vorherige Seite"
      >
        Zurück
      </Button>
      <span className="min-w-24 text-center text-sm text-[#5c5c5c]">
        Seite {Math.min(currentPage, Math.max(1, totalPages))} von{" "}
        {Math.max(1, totalPages)}
      </span>
      <Button
        type="button"
        variant="secondary"
        onClick={() => goTo(currentPage + 1)}
        disabled={currentPage >= totalPages || isPending}
        aria-label="Nächste Seite"
      >
        Weiter
      </Button>
    </div>
  );
}
